export const markSelectedEventAsRead = (events, selectedEventId) => {
  const selectedId = String(selectedEventId || '').trim();
  if (!Array.isArray(events) || !selectedId) return Array.isArray(events) ? events : [];
  return events.map((event) =>
    String(event?.event_uuid || '') === selectedId
      ? { ...event, unreadCount: 0 }
      : event
  );
};

export const getUniqueEventsToAppend = (existingEvents, incomingEvents) => {
  const current = Array.isArray(existingEvents) ? existingEvents : [];
  const incoming = Array.isArray(incomingEvents) ? incomingEvents : [];
  const existingIds = new Set(
    current
      .map((event) => String(event?.event_uuid || '').trim())
      .filter(Boolean)
  );
  return incoming.filter((event) => {
    const eventId = String(event?.event_uuid || '').trim();
    return eventId && !existingIds.has(eventId);
  });
};

export const mergeRefreshTopEvents = ({
  prevEvents,
  topEvents,
  selectedEventId,
}) => {
  const prev = Array.isArray(prevEvents) ? prevEvents : [];
  const top = Array.isArray(topEvents) ? topEvents : [];
  const topMap = new Map(
    top
      .map((event) => [String(event?.event_uuid || '').trim(), event])
      .filter(([eventId]) => Boolean(eventId))
  );
  const others = prev.filter((event) => {
    const eventId = String(event?.event_uuid || '').trim();
    return eventId && !topMap.has(eventId);
  });
  return markSelectedEventAsRead([...top, ...others], selectedEventId);
};

export const buildEventUnreadMap = ({
  rawEvents = [],
  unreadCounts = [],
} = {}) => {
  const unreadMap = {};
  const events = Array.isArray(rawEvents) ? rawEvents : [];
  const counts = Array.isArray(unreadCounts) ? unreadCounts : [];

  events.forEach((event) => {
    const eventId = String(event?.event_uuid || event?.event_id || event?.id || '').trim();
    if (!eventId) return;
    unreadMap[eventId] = Number(event?.unread_count || event?.unreadCount || 0);
  });

  counts.forEach(({ event_id, unread_count }) => {
    const eventId = String(event_id || '').trim();
    if (!eventId) return;
    unreadMap[eventId] = Number(unread_count || 0);
  });

  return unreadMap;
};

export const applySearchResultEvents = ({
  resultEvents = [],
  setEvents,
  setHasMoreEvents,
  setEventsOffset,
} = {}) => {
  if (typeof setEvents === 'function') {
    setEvents(Array.isArray(resultEvents) ? resultEvents : []);
  }
  if (typeof setHasMoreEvents === 'function') {
    setHasMoreEvents(false);
  }
  if (typeof setEventsOffset === 'function') {
    setEventsOffset(0);
  }
};

const resolveHasMoreByComparator = (count, pageLimit, comparator) => {
  const safeCount = Number(count || 0);
  const safeLimit = Number(pageLimit || 0);
  if (!Number.isFinite(safeLimit) || safeLimit <= 0) return false;
  if (comparator === 'gte') return safeCount >= safeLimit;
  return safeCount === safeLimit;
};

export const resolveEventListModeState = ({
  mode = 'reset',
  prevEvents = [],
  incomingEvents = [],
  selectedEventId = null,
  pageLimit = 20,
  refreshTopSliceToLimit = false,
  appendOffsetSource = 'incoming', // 'incoming' | 'added'
  appendHasMoreSource = 'incoming', // 'incoming' | 'added'
  appendHasMoreComparator = 'eq', // 'eq' | 'gte'
  appendJoinSource = 'added', // 'incoming' | 'added'
} = {}) => {
  const safeMode = String(mode || 'reset');
  const prev = Array.isArray(prevEvents) ? prevEvents : [];
  const incoming = Array.isArray(incomingEvents) ? incomingEvents : [];

  if (safeMode === 'append') {
    const addedEvents = getUniqueEventsToAppend(prev, incoming);
    const nextEvents = [...prev, ...addedEvents];
    const offsetIncrement =
      appendOffsetSource === 'added' ? addedEvents.length : incoming.length;
    const hasMoreCount =
      appendHasMoreSource === 'added' ? addedEvents.length : incoming.length;
    return {
      nextEvents,
      joinEvents: appendJoinSource === 'incoming' ? incoming : addedEvents,
      hasMoreEvents: resolveHasMoreByComparator(
        hasMoreCount,
        pageLimit,
        appendHasMoreComparator
      ),
      offsetMode: 'increment',
      offsetValue: offsetIncrement,
    };
  }

  if (safeMode === 'refreshTop') {
    const topEvents = refreshTopSliceToLimit
      ? incoming.slice(0, Math.max(0, Number(pageLimit) || 0))
      : incoming;
    return {
      nextEvents: mergeRefreshTopEvents({
        prevEvents: prev,
        topEvents,
        selectedEventId,
      }),
      joinEvents: topEvents,
      hasMoreEvents: resolveHasMoreByComparator(topEvents.length, pageLimit, 'eq'),
      offsetMode: 'keep',
      offsetValue: 0,
    };
  }

  // reset
  const nextEvents = markSelectedEventAsRead(incoming, selectedEventId);
  return {
    nextEvents,
    joinEvents: nextEvents,
    hasMoreEvents: resolveHasMoreByComparator(nextEvents.length, pageLimit, 'eq'),
    offsetMode: 'set',
    offsetValue: nextEvents.length,
  };
};
